import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Avatar, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Person = ({ u, onUnfollow }) => (
  <li className="flex items-center gap-3 py-2.5" data-testid={`following-person-${u.user_id}`}>
    <Link to={`/u/${u.user_id}`} className="flex min-w-0 flex-1 items-center gap-3 hover:underline"><Avatar user={u} size={36} />
      <span className="min-w-0"><span className="block truncate text-sm font-medium">{u.name}</span>{u.handle && <span className="block truncate text-xs text-slate-400">@{u.handle}</span>}</span></Link>
    <Button size="sm" variant="outline" className="rounded-full" onClick={() => onUnfollow(u)} data-testid={`following-unfollow-${u.user_id}`}>Unfollow</Button>
  </li>
);

// "Following" button: lists the people you follow, each with Unfollow.
export const FollowingButton = ({ onChange }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const show = () => { setOpen(true); setRows(null); api.get("/me/following").then((r) => setRows(r.data.items)).catch(() => setRows([])); };
  const unfollow = async (u) => {
    try { await api.post(`/people/${u.user_id}/unfollow`); setRows((x) => x.filter((y) => y.user_id !== u.user_id)); toast.success(`Unfollowed ${u.name}`); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (<>
    <button type="button" onClick={show} className="chip ml-auto inline-flex items-center gap-1.5" data-testid="feed-following-btn"><Users size={14} />Following</button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="following-dialog">
        <DialogHeader><DialogTitle>People you follow</DialogTitle><DialogDescription>Their Facts appear in your feed, with Middle's check result.</DialogDescription></DialogHeader>
        {rows === null ? <Spinner /> : !rows.length ? <p className="py-4 text-sm text-slate-500" data-testid="following-empty">You're not following anyone yet. Open Member Facts and tap Follow on someone whose Facts you want to see.</p>
          : <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">{rows.map((u) => <Person key={u.user_id} u={u} onUnfollow={unfollow} />)}</ul>}
      </DialogContent>
    </Dialog>
  </>);
};

export const FollowAuthor = ({ p, setP }) => {
  const [busy, setBusy] = useState(false);
  if (p.following_author !== false) return null;
  const follow = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/people/${p.author_id}/follow`); setP((x) => ({ ...x, following_author: true })); toast.success(`Following ${p.author?.name || "them"}. Their Facts will appear in your feed`); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return <button type="button" disabled={busy} onClick={follow} className="rounded-full border border-slate-300 px-2.5 py-0.5 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white" data-testid={`post-follow-author-${p.id}`}>Follow</button>;
};

// Top of Member Facts: members with the most verified Facts in the last 30 days, each with Follow.
export const WhoToFollow = ({ onFollow }) => {
  const [rows, setRows] = useState(null);
  const [done, setDone] = useState({});
  useEffect(() => { api.get("/people/suggested").then((r) => setRows(r.data.items)).catch(() => setRows([])); }, []);
  if (!rows?.length) return null;
  const follow = async (u) => {
    try { await api.post(`/people/${u.user_id}/follow`); setDone((d) => ({ ...d, [u.user_id]: true })); toast.success(`Following ${u.name}. Their Facts will appear in your feed`); onFollow?.(u.user_id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="surface p-4" data-testid="who-to-follow">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Who to follow</p>
      <p className="mb-2 text-xs text-slate-400">Most Facts verified by approved sources in the last 30 days.</p>
      <ul className="divide-y divide-border">{rows.map((u) => (
        <li key={u.user_id} className="flex items-center gap-3 py-2.5" data-testid={`who-to-follow-${u.user_id}`}>
          <Link to={`/u/${u.user_id}`} className="flex min-w-0 flex-1 items-center gap-3 hover:underline"><Avatar user={u} size={36} />
            <span className="min-w-0"><span className="block truncate text-sm font-medium">{u.name}</span>
              <span className="block text-xs text-emerald-700" data-testid={`who-to-follow-count-${u.user_id}`}>{u.verified_recent} verified {u.verified_recent === 1 ? "Fact" : "Facts"}</span></span></Link>
          {done[u.user_id] ? <span className="text-xs font-medium text-emerald-700" data-testid={`who-to-follow-done-${u.user_id}`}>Following</span>
            : <Button size="sm" className="rounded-full" onClick={() => follow(u)} data-testid={`who-to-follow-btn-${u.user_id}`}>Follow</Button>}
        </li>))}</ul>
    </section>
  );
};

const ListRow = ({ u, onToggle }) => (
  <li className="flex items-center gap-3 py-2.5" data-testid={`follow-list-person-${u.user_id}`}>
    <Link to={`/u/${u.user_id}`} className="flex min-w-0 flex-1 items-center gap-3 hover:underline"><Avatar user={u} size={36} />
      <span className="min-w-0"><span className="block truncate text-sm font-medium">{u.name}</span>{u.handle && <span className="block truncate text-xs text-slate-400">@{u.handle}</span>}</span></Link>
    {u.is_me ? <span className="text-xs text-slate-400">You</span>
      : <Button size="sm" variant={u.following ? "outline" : "default"} className="rounded-full" onClick={() => onToggle(u)} data-testid={`follow-list-btn-${u.user_id}`}>{u.following ? "Following" : "Follow"}</Button>}
  </li>
);

// Profile "followers" / "following" counts open this list; each person has Follow / Following.
export const FollowListDialog = ({ userId, side, name, onClose, onFollowChange }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!side) return;
    setRows(null); setError("");
    api.get(`/users/${userId}/${side}`).then((r) => setRows(r.data.items)).catch((e) => { setError(errMsg(e)); setRows([]); });
  }, [userId, side]);
  const toggle = async (u) => {
    try {
      await api.post(`/people/${u.user_id}/${u.following ? "unfollow" : "follow"}`);
      setRows((xs) => xs.map((x) => (x.user_id === u.user_id ? { ...x, following: !u.following } : x)));
      toast.success(u.following ? `Unfollowed ${u.name}` : `Following ${u.name}. Their Facts will appear in your feed`);
      onFollowChange?.(!u.following);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const title = side === "followers" ? `People following ${name}` : `People ${name} follow${name === "you" ? "" : "s"}`;
  return (
    <Dialog open={!!side} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="follow-list-dialog">
        <DialogHeader><DialogTitle data-testid="follow-list-title">{title}</DialogTitle><DialogDescription>Tap a name to see their Facts.</DialogDescription></DialogHeader>
        {rows === null ? <Spinner /> : error ? <p className="py-4 text-sm text-slate-500" data-testid="follow-list-error">{error}</p>
          : !rows.length ? <p className="py-4 text-sm text-slate-500" data-testid="follow-list-empty">{side === "followers" ? "No followers yet." : "Not following anyone yet."}</p>
          : <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto" data-testid="follow-list">{rows.map((u) => <ListRow key={u.user_id} u={u} onToggle={toggle} />)}</ul>}
      </DialogContent>
    </Dialog>
  );
};
